import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import type {
	Geometry,
	GeoJSON,
	MultiPolygon,
	Polygon,
	Position
} from "geojson";
import { isValueNullish } from "../../../utils/valueUtilities";

export type GeoJsonDefinition = string | GeoJSON | null | undefined;

export interface Envelope {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
}

interface PointInPolygonOptions {
	ignoreBoundary?: boolean;
}

export const parseJson = (definition: GeoJsonDefinition): GeoJSON | undefined => {
	if(isValueNullish(definition)) {
		return undefined;
	}

	const parsedDefinition = typeof definition === "string"
		? JSON.parse(definition) as GeoJSON
		: definition;
	return parsedDefinition;
};

export const readGeometries = (definition: GeoJsonDefinition): Geometry[] => {
	const root = parseJson(definition);
	if(!root) {
		return [];
	}

	const type = root.type;
	if(type === "Feature") {
		const geometries = [root.geometry].filter((geometry): geometry is Geometry => !!geometry);
		return geometries;
	}

	if(type === "FeatureCollection") {
		const geometries = (root.features ?? []).reduce<Geometry[]>((geometries, feature) => {
			if(feature?.geometry) {
				geometries.push(feature.geometry);
			}

			return geometries;
		}, []);
		return geometries;
	}

	throw new Error(`Unsupported entity geometry definition type '${type}'.`);
};

export const toExteriorPolygon = (geometry?: Geometry): Polygon | undefined => {
	if(geometry?.type !== "Polygon" || !Array.isArray(geometry.coordinates?.[0])) {
		return undefined;
	}

	return {
		type: "Polygon",
		coordinates: [geometry.coordinates[0]]
	};
};

const flattenNestedCoordinates = (coordinates: unknown): Position[] => {
	if(!Array.isArray(coordinates)) {
		return [];
	}

	if(typeof coordinates[0] === "number") {
		return [coordinates as Position];
	}

	const flattenedCoordinates = coordinates.reduce<Position[]>((all, current) => {
		const nestedCoordinates = flattenNestedCoordinates(current);
		const result = all.concat(nestedCoordinates);
		return result;
	}, []);
	return flattenedCoordinates;
};

export const flattenCoordinates = (geometry?: Geometry): Position[] => {
	if(!geometry) {
		return [];
	}

	if(geometry.type === "Point") {
		return [geometry.coordinates as Position];
	}

	if(geometry.type === "GeometryCollection") {
		const coordinates = (geometry.geometries ?? []).reduce<Position[]>((coordinates, component) => {
			const flattenedCoordinates = flattenCoordinates(component);
			return coordinates.concat(flattenedCoordinates);
		}, []);
		return coordinates;
	}

	const coordinates = flattenNestedCoordinates("coordinates" in geometry ? geometry.coordinates : undefined);
	return coordinates;
};

export const getEnvelope = (geometry?: Geometry): Envelope => {
	const envelope = flattenCoordinates(geometry).reduce<Envelope>((envelope, [x, y]) => ({
		minX: Math.min(envelope.minX, x),
		minY: Math.min(envelope.minY, y),
		maxX: Math.max(envelope.maxX, x),
		maxY: Math.max(envelope.maxY, y)
	}), {
		minX: Infinity,
		minY: Infinity,
		maxX: -Infinity,
		maxY: -Infinity
	});
	return envelope;
};

const isPolygonGeometry = (geometry: Geometry): geometry is Polygon | MultiPolygon =>
	geometry.type === "Polygon" || geometry.type === "MultiPolygon";

const safePointInPolygon = (
	point: Position,
	geometry: Geometry | undefined,
	options: PointInPolygonOptions
): boolean => {
	if(!geometry || !point) {
		return false;
	}

	if(geometry.type === "GeometryCollection") {
		const isCovered = (geometry.geometries ?? []).some(component => safePointInPolygon(point, component, options));
		return isCovered;
	}

	if(!isPolygonGeometry(geometry)) {
		return false;
	}

	const isCovered = booleanPointInPolygon(point, geometry, options);
	return isCovered;
};

export const geometryCoversPoint = (geometry: Geometry | undefined, point: Position): boolean => {
	const isCovered = safePointInPolygon(point, geometry, { ignoreBoundary: false });
	return isCovered;
};

export const geometryContainsPoint = (geometry: Geometry | undefined, point: Position): boolean => {
	const isContained = safePointInPolygon(point, geometry, { ignoreBoundary: true });
	return isContained;
};
